import { Check, CheckCircle2, Copy, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { EscalationOutcome, EscalationRequest } from "@/lib/api";
import { logger } from "@/lib/logger";

import { escalationEmailBody, escalationEmailSubject } from "./utils";

interface BriefConfirmProps {
  draft: EscalationRequest;
  outcome: EscalationOutcome | undefined;
  onConfirm: (confirmed: boolean, note: string) => void;
  isBusy: boolean;
}

/**
 * The tier-3 handoff: the request, written out and ready to copy.
 *
 * This is the whole argument for escalation being a feature rather than a
 * failure — the market gets a precise ask instead of a dead end. The composer
 * does not send it: subject and body sit in their own fields so each can go
 * straight into whatever the market already uses, and both stay editable,
 * because the person sending it is the one who knows their own team.
 */
export function BriefConfirm({ draft, outcome, onConfirm, isBusy }: BriefConfirmProps) {
  const [subject, setSubject] = useState(() => escalationEmailSubject(draft));
  const [body, setBody] = useState(() => escalationEmailBody(draft));
  const settled = outcome !== undefined;
  const sent = outcome?.sent ?? draft.status === "sent";
  const missing = draft.kind === "missing_asset";

  // A later turn reuses this component, so the drafted text has to follow the
  // brief rather than stay on whatever the previous one said.
  useEffect(() => {
    setSubject(escalationEmailSubject(draft));
    setBody(escalationEmailBody(draft));
  }, [draft]);

  const fields: Array<[string, string]> = missing
    ? [
        ["Asked for", draft.requested_change],
        ["Market", draft.target_language],
        ["Needed by", draft.deadline],
      ]
    : [
        ["Asset", draft.asset_filename],
        ["Location", draft.location],
        ["Element", draft.element],
        ["Requested change", draft.requested_change],
        ["Market", draft.target_language],
        ["Needed by", draft.deadline],
      ];

  return (
    <div className="space-y-3">
      <div className="border-outcome-escalated/25 bg-outcome-escalated-tint/50 space-y-3 rounded-xl border p-3">
        <p className="text-outcome-escalated text-xs font-semibold tracking-wide uppercase">
          {sent
            ? "Marked as sent"
            : missing
              ? "Asset request — ready to copy"
              : "Edit request — ready to copy"}
        </p>

        <dl className="grid gap-x-4 gap-y-1.5 text-xs sm:grid-cols-[auto_1fr]">
          {fields.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="text-muted sm:text-right">{label}</dt>
              <dd className="text-foreground font-medium break-words">{value}</dd>
            </div>
          ))}
        </dl>

        <CopyField label="Subject" value={subject}>
          <Input
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            className="bg-card text-xs"
            aria-label="Email subject"
          />
        </CopyField>

        <CopyField label="Message" value={body}>
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={14}
            className="bg-card font-mono text-xs leading-5"
            aria-label="Email body"
          />
        </CopyField>
      </div>

      {settled ? (
        <p className="text-muted flex items-center gap-1.5 text-sm">
          {sent ? (
            <CheckCircle2 className="text-outcome-applied h-4 w-4" />
          ) : (
            <X className="h-4 w-4" />
          )}
          {outcome?.message}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-muted mr-auto text-xs">
            Send it from your own mail client — the composer only drafts it.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onConfirm(true, subject)}
            disabled={isBusy}
          >
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {isBusy ? "Recording…" : "Mark as sent"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onConfirm(false, "")} disabled={isBusy}>
            Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One copyable field: its label, its own copy button, and the control itself.
 *
 * The button belongs to the field rather than the panel — the subject and the
 * body go to two different places, and one "Copy" for both meant pasting the
 * whole thing somewhere and then deleting half of it.
 */
function CopyField({
  label,
  value,
  children,
}: {
  label: string;
  value: string;
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error("Could not copy the brief field", error);
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="type-label text-muted">{label}</span>
        <Button variant="ghost" size="sm" onClick={copy} aria-label={`Copy ${label.toLowerCase()}`}>
          {copied ? <Check className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      {children}
    </div>
  );
}
